import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, RefreshCw, Clock, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { ticketService } from '../../services/ticketService';
import LoadingSpinner from '../../components/LoadingSpinner';
import TicketCard from './components/TicketCard';
import TicketDetailModal from './components/TicketDetailModal';
import CreateTicketModal from './components/CreateTicketModal';
import StatsOverview from './components/StatsOverview';

const AgentWorkspace = () => {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showMyTickets, setShowMyTickets] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    loadTickets();
    loadStatistics();
  }, [showMyTickets]);

  useEffect(() => {
    applyFilters();
  }, [tickets, searchQuery, selectedStatus, selectedPriority, selectedCategory]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError('');
      const filters = {};
      if (showMyTickets) {
        filters.assignedToMe = true;
      }
      const data = await ticketService?.getAll(filters);
      setTickets(data);
    } catch (err) {
      console.error('Failed to load tickets:', err);
      setError(err?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await ticketService?.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Failed to load statistics:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...tickets];

    if (searchQuery) {
      filtered = filtered?.filter(ticket =>
        ticket?.subject?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        ticket?.ticketNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        ticket?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    if (selectedStatus !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.status === selectedStatus);
    }

    if (selectedPriority !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.priority === selectedPriority);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(ticket => ticket?.category === selectedCategory);
    }

    setFilteredTickets(filtered);
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleTicketUpdate = async (updatedTicket) => {
    setTickets(prevTickets =>
      prevTickets?.map(ticket =>
        ticket?.id === updatedTicket?.id ? { ...ticket, ...updatedTicket } : ticket
      )
    );
    await loadStatistics();
  };

  const handleCreateTicket = async (ticketData) => {
    try {
      await ticketService?.create(ticketData);
      setShowCreateModal(false);
      await loadTickets();
      await loadStatistics();
    } catch (err) {
      console.error('Failed to create ticket:', err);
      throw err;
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-blue-600 bg-blue-50',
      medium: 'text-yellow-600 bg-yellow-50',
      high: 'text-orange-600 bg-orange-50',
      urgent: 'text-red-600 bg-red-50'
    };
    return colors?.[priority] || colors?.medium;
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="w-4 h-4" />,
      in_progress: <Clock className="w-4 h-4" />,
      resolved: <CheckCircle className="w-4 h-4" />,
      closed: <XCircle className="w-4 h-4" />
    };
    return icons?.[status] || icons?.open;
  };

  if (loading && !tickets?.length) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agent Workspace</h1>
          <p className="text-gray-600 mt-1">Manage and resolve customer support tickets</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadTickets}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Ticket
          </button>
        </div>
      </div>
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">Error loading tickets</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}
      {/* Statistics */}
      {statistics && <StatsOverview statistics={statistics} />}
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search tickets by number, subject, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="pending_user">Pending User</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          {/* Priority Filter */}
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Categories</option>
            <option value="technical">Technical</option>
            <option value="billing">Billing</option>
            <option value="account">Account</option>
            <option value="feature_request">Feature Request</option>
            <option value="bug_report">Bug Report</option>
            <option value="other">Other</option>
          </select>

          {/* My Tickets Toggle */}
          <button
            onClick={() => setShowMyTickets(!showMyTickets)}
            className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              showMyTickets
                ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {showMyTickets ? 'All Tickets' : 'My Tickets'}
          </button>
        </div>
      </div>
      {/* Tickets List */}
      <div className="space-y-4">
        {filteredTickets?.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <Filter className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg font-medium">No tickets found</p>
            <p className="text-gray-500 mt-2">
              {tickets?.length === 0
                ? 'No tickets have been created yet' :'Try adjusting your filters'}
            </p>
          </div>
        ) : (
          filteredTickets?.map(ticket => (
            <TicketCard
              key={ticket?.id}
              ticket={ticket}
              onClick={() => handleTicketClick(ticket)}
              getPriorityColor={getPriorityColor}
              getStatusIcon={getStatusIcon}
            />
          ))
        )}
      </div>
      {/* Modals */}
      {selectedTicket && (
        <TicketDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdate={handleTicketUpdate}
        />
      )}
      {showCreateModal && (
        <CreateTicketModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTicket}
        />
      )}
    </div>
  );
};

export default AgentWorkspace;